"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PLAForm = void 0;
const __1 = require("..");
class PLAForm {
    static isPLAForm(form) {
        return form.metadata.isPLAForm();
    }
    constructor(form) {
        this.form = form;
        if (!PLAForm.isPLAForm(form)) {
            throw new Error('Form is not a PLA form');
        }
    }
    get type() {
        if (this.form.metadata.options.spidacalc) {
            return 'spidacalc';
        }
        if (this.form.metadata.options.dds) {
            return 'dds';
        }
        if (this.form.metadata.options.ikePoleForeman) {
            return 'ikePoleForeman';
        }
        if (this.form.metadata.options.ocalc) {
            return 'ocalc';
        }
        if (this.form.metadata.options.poleforeman) {
            return 'poleforeman';
        }
        throw new Error('Form is not a PLA form');
    }
    getField(subformInstance, ikePoleFieldIdsId, ikePoleForemanFieldId) {
        switch (this.type) {
            case 'dds':
            case 'ocalc':
            case 'spidacalc':
            case 'poleforeman':
                return subformInstance.getOptionalField(ikePoleFieldIdsId);
            case 'ikePoleForeman':
                if (!ikePoleForemanFieldId) {
                    return null;
                }
                return subformInstance.getOptionalField(ikePoleForemanFieldId);
            default:
                throw new Error(`Unrecognized form type: ${this.type}`);
        }
    }
    get statusField() {
        return this.getField(this.form, __1.PoleFieldIds.STATUS, __1.IkePoleForemanFieldIds.STATUS);
    }
    get sequenceField() {
        return null;
    }
    get idField() {
        return this.getField(this.form, __1.PoleFieldIds.POLE_ID, __1.IkePoleForemanFieldIds.ID);
    }
    get tagPhotosField() {
        return this.getField(this.form, __1.PoleFieldIds.IMAGES, __1.IkePoleForemanFieldIds.TAG_PHOTOS);
    }
    get typeField() {
        return this.getField(this.form, __1.PoleFieldIds.POLE_TYPE, __1.IkePoleForemanFieldIds.TYPE);
    }
    get ownerField() {
        return this.getField(this.form, __1.PoleFieldIds.POLE_OWNER, __1.IkePoleForemanFieldIds.OWNER);
    }
    get circumferenceField() {
        return this.getField(this.form, __1.PoleFieldIds.CIRCUMFERENCE, null);
    }
    get tipField() {
        return this.getField(this.form, __1.PoleFieldIds.AGL, __1.IkePoleForemanFieldIds.TIP);
    }
    get lowPowerAttachmentField() {
        return null;
    }
    get locationField() {
        return this.getField(this.form, __1.PoleFieldIds.GEOGRAPHIC_COORDINATE, __1.IkePoleForemanFieldIds.LOCATION);
    }
    get streetAddressField() {
        return null;
    }
    get noteField() {
        return this.getField(this.form, __1.PoleFieldIds.NOTE, __1.IkePoleForemanFieldIds.NOTE);
    }
    get ikePhotoField() {
        return this.getField(this.form, __1.PoleFieldIds.IKE_PHOTO, __1.IkePoleForemanFieldIds.IKE_PHOTO);
    }
    get lowPowerToCommField() {
        return null;
    }
    get equipmentField() {
        return this.getField(this.form, __1.PoleFieldIds.EQUIPMENT.FORM, __1.IkePoleForemanFieldIds.EQUIPMENT);
    }
    get anchorField() {
        return this.getField(this.form, __1.PoleFieldIds.ANCHOR.FORM, __1.IkePoleForemanFieldIds.ANCHOR);
    }
    get spanField() {
        return this.getField(this.form, __1.PoleFieldIds.SPAN.FORM, __1.IkePoleForemanFieldIds.SPAN);
    }
    get spanTypeField() {
        return this.getField(this.spanField, __1.PoleFieldIds.SPAN.TYPE, __1.IkePoleForemanFieldIds.SPAN_TYPE);
    }
    get spanTypeMapping() {
        if (this.type === 'ikePoleForeman') {
            return {
                [__1.IPFSpanType.FORE_SPAN]: __1.IPFSpanType.BACK_SPAN,
                [__1.IPFSpanType.BACK_SPAN]: __1.IPFSpanType.FORE_SPAN,
                [__1.IPFSpanType.OTHER]: __1.IPFSpanType.OTHER,
            };
        }
        else {
            return {
                [__1.SpanType.NextPole]: __1.SpanType.PreviousPole,
                [__1.SpanType.PreviousPole]: __1.SpanType.NextPole,
                [__1.SpanType.Other]: __1.SpanType.Other,
            };
        }
    }
    get spanVectorField() {
        return this.getField(this.spanField, __1.PoleFieldIds.SPAN.LENGTH, __1.IkePoleForemanFieldIds.SPAN_LENGTH);
    }
    get spanMidSpanIkePhoto() {
        return this.getField(this.spanField, __1.PoleFieldIds.SPAN.MID_SPAN_PHOTO, __1.IkePoleForemanFieldIds.SPAN_MID_SPAN_IKE_PHOTO);
    }
    get spanAttachmentHeightFields() {
        const fieldIds = [];
        // IPF has a completely different structure with different fields
        if (this.type === 'ikePoleForeman') {
            fieldIds.push(__1.IkePoleForemanFieldIds.SPAN_GUY_ATTACHMENT_HEIGHT, __1.IkePoleForemanFieldIds.SPAN_COMMUNICATION_MID_SPAN_HEIGHT, __1.IkePoleForemanFieldIds.SPAN_COMMUNICATION_ATTACHMENT_HEIGHT, __1.IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_PRIMARY_PHASE_A_HEIGHT, __1.IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_PRIMARY_PHASE_B_HEIGHT, __1.IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_PRIMARY_PHASE_C_HEIGHT, __1.IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_NEUTRAL_HEIGHT, __1.IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_SECONDARY_PHASE_A_HEIGHT, __1.IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_SECONDARY_PHASE_B_HEIGHT, __1.IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_SECONDARY_PHASE_C_HEIGHT, __1.IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_SHIELD_WIRE_HEIGHT);
        }
        else {
            // The following are mostly standard across all other PLA forms
            fieldIds.push(__1.PoleFieldIds.WIRE.ATTACHMENT_HEIGHT, __1.PoleFieldIds.SPAN_GUY.ATTACHMENT_HEIGHT, __1.PoleFieldIds.SPAN_GUY.MID_SPAN_HEIGHT, __1.PoleFieldIds.SPAN_GUY.HEIGHT_AT_WEP);
            // DDS has additional field which the other old PLA forms don't have
            if (this.type === 'dds') {
                fieldIds.push(__1.DDSFieldIds.JOINT_USE_CABLE.ATTACHMENT_HEIGHT);
            }
            // Legacy poleforeman has additional field which the other old PLA forms don't have
            if (this.type === 'poleforeman') {
                fieldIds.push(__1.LegacyPoleForemanFieldIds.PHASE_A_HEIGHT, __1.LegacyPoleForemanFieldIds.PHASE_B_HEIGHT, __1.LegacyPoleForemanFieldIds.PHASE_C_HEIGHT);
            }
        }
        // Use findField to burrow into nested lists and to safely handle missing fields, filter out nulls
        return fieldIds.map((id) => this.spanField.findField(id)).filter((field) => field != null);
    }
    enableSpanSubform() {
        this.spanField.Mutator.span.enable();
    }
    spanValue(instance) {
        return new __1.PLASpanAdaptor(instance, this);
    }
}
exports.PLAForm = PLAForm;
//# sourceMappingURL=PLAForm.js.map